import { DraftCondition, GroupedConditions } from '@retail/calculus/types';
import { useCallback, useState } from 'react';

interface Props {
  draftConditions: DraftCondition[];
  groupedConditions: GroupedConditions[];
}

export const useCalculusConditionSelectionActions = ({
  draftConditions,
  groupedConditions,
}: Props) => {
  const [selectedConditionIds, setSelectedConditionIds] = useState<number[]>([]);

  const handleSelectConditionId = useCallback(
    (conditionId: number) =>
      selectedConditionIds.includes(conditionId)
        ? setSelectedConditionIds(selectedConditionIds.filter((condId) => condId !== conditionId))
        : setSelectedConditionIds([...selectedConditionIds, conditionId]),
    [selectedConditionIds]
  );

  const handleSelectAllConditionIdsForSupplierOrAssortment = useCallback(
    (assortmentOrSupplierId: number) => {
      const groupConditions = groupedConditions.find(
        (group) => group.assortmentOrSupplierId === assortmentOrSupplierId
      );
      const conditionIds =
        groupConditions?.conditions
          .map((cond) => cond.condition.types.map((type) => type.id))
          .flat() ?? [];

      const checkedIds = selectedConditionIds.filter((condId) => conditionIds.includes(condId));

      if (checkedIds.length === conditionIds.length) {
        setSelectedConditionIds([]);
      } else {
        setSelectedConditionIds(Array.from(new Set([...conditionIds, ...selectedConditionIds])));
      }
    },
    [groupedConditions, selectedConditionIds]
  );

  const handleSelectAllConditions = useCallback(() => {
    const allConditionIds = draftConditions
      .map((cond) => cond.condition.types.map((type) => type.id))
      .flat();
    allConditionIds.length === selectedConditionIds.length
      ? setSelectedConditionIds([])
      : setSelectedConditionIds(allConditionIds);
  }, [draftConditions, selectedConditionIds.length]);

  return {
    selectedConditionIds,
    setSelectedConditionIds,
    handleSelectConditionId,
    handleSelectAllConditionIdsForSupplierOrAssortment,
    handleSelectAllConditions,
  };
};
